import React, { createContext, useContext, useState, useEffect } from "react";
import { message } from "antd";
import api from "../utils/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      console.log("🔍 AuthContext: Starting auth check...");
      const token = localStorage.getItem("admin_token");
      const userData = localStorage.getItem("admin_user");

      // No token = public visitor, set loading false immediately
      if (!token || !userData) {
        console.log("✅ AuthContext: No token found - public visitor");
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      // Has token = verify it
      console.log("🔐 AuthContext: Token found, verifying...");
      try {
        const parsedUser = JSON.parse(userData);

        // Set the token in the API headers
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // Verify token by making a test request - skip error toast to prevent redirect loop
        await api.get("/admin/dashboard", { skipErrorToast: true });

        console.log("✅ AuthContext: Token valid, user authenticated");
        if (isMounted) {
          setUser(parsedUser);
          setLoading(false);
        }
      } catch (error) {
        console.log("❌ AuthContext: Token verification failed, clearing storage");
        // Token is invalid/expired - clear it silently
        if (isMounted) {
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_user");
          delete api.defaults.headers.common["Authorization"];
          setUser(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/admin/login", {
        email,
        password,
      });

      const { token, user: userData } = response.data;

      // Store token and user data
      localStorage.setItem("admin_token", token);
      localStorage.setItem("admin_user", JSON.stringify(userData));

      // Set token in API headers
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setUser(userData);
      message.success("Login successful!");

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Login failed";
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
    message.success("Logged out successfully");
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem("admin_user", JSON.stringify(updatedUser));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
